/**
 * QA maturity check. Verbatim from design/Homepage.dc.html — the QS and BANDS
 * constants at lines 512-526 and the markup at 347-392.
 *
 * Bands are the output, never a numeric score per dimension. Six questions
 * scored 1-5 give a 6-30 total.
 */

export const maturitySection = {
  eyebrow: "Interactive · 60 seconds",
  heading: "QA maturity check",
  lead: "Six questions, scored 1 (not at all) to 5 (fully in place). You get a maturity band and the moves we would make first. No email required.",
  outOf: "out of 30",
  movesLabel: "What we would do first",
  cta: { label: "Get this reviewed by an engineer", href: "#book" },
  scaleLow: "Not at all",
  scaleHigh: "Fully in place",
} as const;

export type Question = { label: string; hint: string };

export const questions: Question[] = [
  {
    label: "Automated regression coverage",
    hint: "Critical user journeys are covered by automated tests that run on every release candidate.",
  },
  {
    label: "Tests run in CI, not on laptops",
    hint: "The suite is wired into CI/CD and blocks a merge or a release when it fails.",
  },
  {
    label: "Test data & environments",
    hint: "Environments are reproducible and test data is provisioned without manual setup.",
  },
  {
    label: "Flake and failure triage",
    hint: "Failures are triaged to a root cause quickly, and flaky tests are tracked rather than ignored.",
  },
  {
    label: "AI output validation",
    hint: "Non-deterministic features are benchmarked for accuracy, bias, privacy and injection resistance.",
  },
  {
    label: "Compliance evidence",
    hint: "Audit evidence for HIPAA, SOC 2, PCI-DSS or FDA is produced by the pipeline, not assembled by hand.",
  },
];

export const DEFAULT_SCORES = [3, 3, 2, 3, 1, 2];

export type Band = {
  max: number;
  band: string;
  note: string;
  moves: string[];
};

export const bands: Band[] = [
  {
    max: 11,
    band: "Reactive",
    note: "Quality is discovered late, usually by users. The fastest win is a thin automated regression layer wired into CI so regressions stop reaching production.",
    moves: [
      "Audit current coverage and release blockers",
      "Automate the top 20 revenue-critical journeys",
      "Add a single blocking quality gate in CI",
    ],
  },
  {
    max: 18,
    band: "Repeatable",
    note: "The basics run, but they depend on people remembering. Stabilise environments and triage, then widen coverage without widening maintenance.",
    moves: [
      "Reproducible environments and seeded test data",
      "Flake tracking with an owner and an SLA",
      "Extend API and contract coverage before UI",
    ],
  },
  {
    max: 24,
    band: "Managed",
    note: "Solid engineering discipline. The gap is usually non-deterministic behaviour and audit evidence produced by hand.",
    moves: [
      "Benchmark AI outputs for accuracy and bias",
      "Automate compliance evidence collection",
      "Performance and load baselines per release",
    ],
  },
  {
    max: 30,
    band: "Optimised",
    note: "You are ahead of most regulated teams. Value now comes from prediction and cost: risk-based selection, environment spend and adversarial testing.",
    moves: [
      "Risk-based test selection from defect history",
      "Adversarial and prompt-injection suites",
      "Environment consolidation to cut infrastructure spend",
    ],
  },
];

export function bandFor(total: number): Band {
  return bands.find((b) => total <= b.max) ?? bands[bands.length - 1];
}
